import { createFileRoute, Link } from "@tanstack/react-router";
import { getErrorMessage } from "@/lib/errorMessage";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { confirmEmailChange } from "@/lib/account/email.functions";

export const Route = createFileRoute("/verify-email")({
  head: () => ({
    meta: [
      { title: "Confirm your email · TruWe" },
      {
        name: "description",
        content: "Confirm the email address on your TruWe account to receive order updates and receipts.",
      },
      { property: "og:title", content: "Confirm your email · TruWe" },
      {
        property: "og:description",
        content: "Confirm the email address on your TruWe account.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
  errorComponent: ({ error }: { error: unknown }) => (
    <Shell title="Something went wrong">
      <p className="text-sm text-muted-foreground">{getErrorMessage(error)}</p>
    </Shell>
  ),
  notFoundComponent: () => <Shell title="Not found">{null}</Shell>,
  component: VerifyEmailPage,
});

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <main className="min-h-[calc(100vh-4rem)] bg-[radial-gradient(ellipse_at_top,theme(colors.brand-soft/40),transparent_60%)]">
      <div className="mx-auto flex max-w-lg flex-col items-center px-4 py-20 text-center">
        <div className="w-full rounded-3xl border border-border/70 bg-card/80 p-7 shadow-[0_20px_60px_-40px_rgba(30,94,255,0.35)] backdrop-blur-sm">
          <h1 className="font-display text-2xl font-semibold text-brand-deep">{title}</h1>
          <div className="mt-3">{children}</div>
        </div>
      </div>
    </main>
  );
}

function VerifyEmailPage() {
  const { token } = Route.useSearch();
  const confirm = useServerFn(confirmEmailChange);
  const started = useRef(false);
  const [state, setState] = useState<
    { status: "loading" } | { status: "ok"; email: string } | { status: "error"; message: string }
  >({ status: "loading" });

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!token) {
      setState({ status: "error", message: "This verification link is incomplete." });
      return;
    }
    (async () => {
      try {
        const res = await confirm({ data: { token } });
        if (res.ok && res.email) {
          setState({ status: "ok", email: res.email });
          toast.success("Email confirmed", {
            description: `${res.email} is now the email on your TruWe account. A confirmation email is on its way.`,
          });
        } else {
          const message = res.error || "This link is no longer valid.";
          setState({ status: "error", message });
          toast.error("Couldn't confirm email", { description: message });
        }
      } catch {
        setState({ status: "error", message: "We couldn't verify this link. Please try again." });
        toast.error("Couldn't confirm email", { description: "Please try again." });
      }
    })();
  }, [token, confirm]);

  if (state.status === "loading") {
    return (
      <Shell title="Confirming your email">
        <Loader2 className="mx-auto animate-spin text-brand" />
        <p className="mt-3 text-sm text-muted-foreground">Just a moment…</p>
      </Shell>
    );
  }

  if (state.status === "error") {
    return (
      <Shell title="Link not valid">
        <XCircle className="mx-auto text-destructive" />
        <p className="mt-3 text-sm text-muted-foreground">{state.message}</p>
        <Button asChild className="mt-5 rounded-full">
          <Link to="/account">Go to my account</Link>
        </Button>
      </Shell>
    );
  }

  return (
    <Shell title="Email confirmed">
      <CheckCircle2 className="mx-auto text-brand" />
      <p className="mt-3 text-sm text-muted-foreground">
        <span className="font-medium text-brand-deep">{state.email}</span> is now the email on your
        TruWe account. Order confirmations and receipts will go here.
      </p>
      <Button asChild className="mt-5 rounded-full">
        <Link to="/account">Go to my account</Link>
      </Button>
    </Shell>
  );
}
